import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Scale, FileText, Clock, CheckCircle, XCircle, AlertTriangle, TrendingUp, Send, Eye, ChevronDown, ChevronUp, Loader, HelpCircle, DollarSign, Calendar, Building, ExternalLink, Shield } from 'lucide-react';
import api from '../services/api';
import DisclaimerLegalRecursos from '../components/DisclaimerLegalRecursos';
import RecurribilityBadge, { RecurribilityCard } from '../components/RecurribilityBadge';
import FuenteVerificada, { ListaFuentesVerificadas, VerificacionCitasBadge } from '../components/FuenteVerificada';

function Recursos() {
  const navigate = useNavigate();
  const [recursos, setRecursos] = useState([]);
  const [estadisticas, setEstadisticas] = useState(null);
  const [loading, setLoading] = useState(true);
  const [multasPendientes, setMultasPendientes] = useState([]);
  const [selectedMulta, setSelectedMulta] = useState(null);
  const [analisis, setAnalisis] = useState(null);
  const [analizando, setAnalizando] = useState(false);
  const [solicitando, setSolicitando] = useState(false);
  const [vista, setVista] = useState('lista'); // lista, analizar, detalle
  const [recursoDetalle, setRecursoDetalle] = useState(null);
  const [expandido, setExpandido] = useState({});
  // Estado para disclaimer
  const [mostrarDisclaimer, setMostrarDisclaimer] = useState(false);
  const [disclaimerAceptacionId, setDisclaimerAceptacionId] = useState(null);

  useEffect(() => {
    cargarDatos();
  }, []);

  const cargarDatos = async () => {
    try {
      setLoading(true);

      const [recursosRes, estadisticasRes, multasRes] = await Promise.all([
        api.get('/recursos/mis-recursos'),
        api.get('/recursos/estadisticas/empresa'),
        api.get('/multas?estado=pendiente_identificacion').catch(() => ({ data: { multas: [] } }))
      ]);

      setRecursos(recursosRes.data.recursos || []);
      setEstadisticas(estadisticasRes.data.estadisticas);
      setMultasPendientes(multasRes.data.multas || []);
    } catch (error) {
      console.error('Error cargando datos:', error);
    } finally {
      setLoading(false);
    }
  };

  const analizarMulta = async (multa) => {
    try {
      setAnalizando(true);
      setSelectedMulta(multa);
      setVista('analizar');

      const response = await api.get(`/recursos/analizar/${multa.id}`);

      setAnalisis(response.data.analisis);
    } catch (error) {
      console.error('Error analizando multa:', error);
      alert('Error al analizar la multa');
    } finally {
      setAnalizando(false);
    }
  };

  // Iniciar proceso de solicitud (muestra disclaimer)
  const iniciarSolicitudRecurso = () => {
    if (!selectedMulta) return;
    setMostrarDisclaimer(true);
  };

  // Callback cuando se acepta el disclaimer
  const handleDisclaimerAceptado = async (aceptacionId) => {
    setDisclaimerAceptacionId(aceptacionId);
    setMostrarDisclaimer(false);

    // Proceder con la solicitud
    await solicitarRecursoConDisclaimer(aceptacionId);
  };

  // Solicitar recurso con disclaimer ya aceptado
  const solicitarRecursoConDisclaimer = async (aceptacionId) => {
    if (!selectedMulta) return;

    try {
      setSolicitando(true);

      await api.post('/recursos/solicitar', {
        multa_id: selectedMulta.id,
        usar_ia: true,
        disclaimer_aceptacion_id: aceptacionId
      });

      alert('Recurso solicitado correctamente. Sera revisado por nuestros abogados.');
      setVista('lista');
      setSelectedMulta(null);
      setAnalisis(null);
      setDisclaimerAceptacionId(null);
      cargarDatos();
    } catch (error) {
      console.error('Error solicitando recurso:', error);
      alert(error.response?.data?.error || 'Error al solicitar el recurso');
    } finally {
      setSolicitando(false);
    }
  };

  // Version legacy (sin disclaimer - deprecado)
  const solicitarRecurso = async () => {
    if (!selectedMulta) return;
    // Mostrar disclaimer primero
    iniciarSolicitudRecurso();
  };

  const verDetalleRecurso = async (recurso) => {
    try {
      const response = await api.get(`/recursos/${recurso.id}`);
      setRecursoDetalle(response.data.recurso);
      setVista('detalle');
    } catch (error) {
      console.error('Error cargando detalle:', error);
    }
  };

  const getEstadoBadge = (estado) => {
    const estados = {
      solicitado: { color: 'bg-blue-100 text-blue-800', icon: Clock, label: 'Solicitado' },
      en_revision: { color: 'bg-yellow-100 text-yellow-800', icon: Eye, label: 'En Revisión' },
      aprobado: { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Aprobado' },
      rechazado: { color: 'bg-red-100 text-red-800', icon: XCircle, label: 'Rechazado' },
      enviado: { color: 'bg-purple-100 text-purple-800', icon: Send, label: 'Enviado' },
      resuelto: { color: 'bg-gray-100 text-gray-800', icon: CheckCircle, label: 'Resuelto' }
    };
    const config = estados[estado] || estados.solicitado;
    const Icon = config.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-3 h-3" /> {config.label}
      </span>
    );
  };

  const getProbabilidadColor = (prob) => {
    if (prob >= 70) return 'text-green-600';
    if (prob >= 50) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getProbabilidadBg = (prob) => {
    if (prob >= 70) return 'bg-green-500';
    if (prob >= 50) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  // Vista de análisis de multa
  if (vista === 'analizar' && selectedMulta) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-4xl mx-auto">
          <button onClick={() => { setVista('lista'); setAnalisis(null); }} className="flex items-center gap-2 text-blue-600 hover:text-blue-800 mb-6">
            <ArrowLeft className="w-4 h-4" /> Volver
          </button>

          <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Análisis de Viabilidad del Recurso</h2>
            
            {/* Datos de la multa */}
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <h3 className="font-medium text-gray-700 mb-2">Datos de la Multa</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div><span className="text-gray-500">Matrícula:</span> <span className="font-medium">{selectedMulta.matricula}</span></div>
                <div><span className="text-gray-500">Fecha:</span> <span className="font-medium">{new Date(selectedMulta.fecha_infraccion).toLocaleDateString()}</span></div>
                <div><span className="text-gray-500">Importe:</span> <span className="font-medium">{selectedMulta.importe}€</span></div>
                <div><span className="text-gray-500">Puntos:</span> <span className="font-medium">{selectedMulta.puntos || 0}</span></div>
              </div>
              {selectedMulta.descripcion && (
                <p className="mt-2 text-sm text-gray-600">{selectedMulta.descripcion}</p>
              )}
            </div>

            {analizando ? (
              <div className="flex flex-col items-center justify-center py-12">
                <Loader className="w-12 h-12 text-blue-500 animate-spin mb-4" />
                <p className="text-gray-600">Analizando viabilidad del recurso...</p>
                <p className="text-sm text-gray-400 mt-2">Esto puede tardar unos segundos</p>
              </div>
            ) : analisis ? (
              <>
                {/* Card de Recurribilidad mejorada */}
                <RecurribilityCard
                  probabilidad={analisis.probabilidad_exito}
                  factoresPositivos={analisis.factores_positivos}
                  factoresNegativos={analisis.factores_negativos}
                />

                {/* Aviso sobre fuentes verificadas */}
                {analisis.analizado_por_ia && (
                  <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <Shield className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <h4 className="font-medium text-blue-900">Analisis con Fuentes Verificadas</h4>
                        <p className="text-sm text-blue-700 mt-1">
                          Este analisis utiliza citas legales del BOE y CENDOJ con URLs verificables.
                          Un abogado colegiado revisara el recurso antes de su presentacion.
                        </p>
                        {analisis.tiene_argumentos_sin_fuente && (
                          <p className="text-sm text-amber-700 mt-2 flex items-center gap-1">
                            <AlertTriangle className="w-4 h-4" />
                            Algunos argumentos requieren verificacion adicional por el abogado.
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Factores */}
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  {/* Factores positivos */}
                  <div>
                    <h3 className="font-medium text-green-700 mb-3 flex items-center gap-2">
                      <TrendingUp className="w-4 h-4" /> Factores a Favor
                    </h3>
                    <div className="space-y-2">
                      {analisis.factores_positivos?.map((factor, i) => (
                        <div key={i} className="bg-green-50 border border-green-200 rounded-lg p-3">
                          <div className="flex justify-between items-start">
                            <span className="font-medium text-green-800">{factor.factor}</span>
                            <span className="text-green-600 text-sm">{factor.impacto}</span>
                          </div>
                          {factor.detalle && <p className="text-sm text-green-700 mt-1">{factor.detalle}</p>}
                        </div>
                      ))}
                      {(!analisis.factores_positivos || analisis.factores_positivos.length === 0) && (
                        <p className="text-gray-500 text-sm">No se identificaron factores positivos destacables</p>
                      )}
                    </div>
                  </div>

                  {/* Factores negativos */}
                  <div>
                    <h3 className="font-medium text-red-700 mb-3 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" /> Factores en Contra
                    </h3>
                    <div className="space-y-2">
                      {analisis.factores_negativos?.map((factor, i) => (
                        <div key={i} className="bg-red-50 border border-red-200 rounded-lg p-3">
                          <div className="flex justify-between items-start">
                            <span className="font-medium text-red-800">{factor.factor}</span>
                            <span className="text-red-600 text-sm">{factor.impacto}</span>
                          </div>
                          {factor.detalle && <p className="text-sm text-red-700 mt-1">{factor.detalle}</p>}
                        </div>
                      ))}
                      {(!analisis.factores_negativos || analisis.factores_negativos.length === 0) && (
                        <p className="text-gray-500 text-sm">No se identificaron factores negativos destacables</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Coste y ROI */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-medium text-blue-900">Coste del Recurso</h3>
                      <p className="text-sm text-blue-700">Incluye análisis, redacción y presentación</p>
                    </div>
                    <div className="text-right">
                      <span className="text-2xl font-bold text-blue-900">25€</span>
                      {selectedMulta.importe >= 100 && (
                        <p className="text-sm text-blue-600">
                          ROI potencial: {Math.round((selectedMulta.importe / 25) * (analisis.probabilidad_exito / 100))}x
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Botones */}
                <div className="flex gap-4">
                  <button
                    onClick={iniciarSolicitudRecurso}
                    disabled={solicitando}
                    className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium"
                  >
                    {solicitando ? <Loader className="w-5 h-5 animate-spin" /> : <Scale className="w-5 h-5" />}
                    {solicitando ? 'Solicitando...' : 'Solicitar Recurso (25€)'}
                  </button>
                  <button
                    onClick={() => { setVista('lista'); setAnalisis(null); }}
                    className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                  >
                    Cancelar
                  </button>
                </div>

                {/* Modal de Disclaimer */}
                <DisclaimerLegalRecursos
                  isOpen={mostrarDisclaimer}
                  onClose={() => setMostrarDisclaimer(false)}
                  onAccept={handleDisclaimerAceptado}
                  multaId={selectedMulta?.id}
                />
              </>
            ) : null}
          </div>
        </div>
      </div>
    );
  }

  // Vista de detalle de recurso
  if (vista === 'detalle' && recursoDetalle) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-4xl mx-auto">
          <button onClick={() => { setVista('lista'); setRecursoDetalle(null); }} className="flex items-center gap-2 text-blue-600 hover:text-blue-800 mb-6">
            <ArrowLeft className="w-4 h-4" /> Volver
          </button>

          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">Recurso #{recursoDetalle.id}</h2>
              {getEstadoBadge(recursoDetalle.estado)}
            </div>

            {/* Timeline de estados */}
            <div className="mb-6">
              <div className="flex items-center gap-4 text-sm">
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                  <span>Solicitado: {new Date(recursoDetalle.created_at).toLocaleDateString()}</span>
                </div>
                {recursoDetalle.fecha_revision && (
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                    <span>Revisado: {new Date(recursoDetalle.fecha_revision).toLocaleDateString()}</span>
                  </div>
                )}
                {recursoDetalle.fecha_envio && (
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded-full bg-purple-500"></div>
                    <span>Enviado: {new Date(recursoDetalle.fecha_envio).toLocaleDateString()}</span>
                  </div>
                )}
                {recursoDetalle.fecha_resolucion && (
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                    <span>Resuelto: {new Date(recursoDetalle.fecha_resolucion).toLocaleDateString()}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Datos de la multa */}
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <h3 className="font-medium text-gray-700 mb-2">Datos de la Multa</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div><span className="text-gray-500">Matrícula:</span> <span className="font-medium">{recursoDetalle.matricula}</span></div>
                <div><span className="text-gray-500">Fecha:</span> <span className="font-medium">{new Date(recursoDetalle.fecha_infraccion).toLocaleDateString()}</span></div>
                <div><span className="text-gray-500">Importe:</span> <span className="font-medium">{recursoDetalle.importe_multa}€</span></div>
                <div><span className="text-gray-500">Probabilidad:</span> <span className={`font-medium ${getProbabilidadColor(recursoDetalle.probabilidad_exito)}`}>{recursoDetalle.probabilidad_exito}%</span></div>
              </div>
            </div>

            {/* Resolución si existe */}
            {recursoDetalle.resolucion && (
              <div className={`rounded-lg p-4 mb-6 ${recursoDetalle.resolucion === 'estimado' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                <h3 className={`font-medium mb-2 ${recursoDetalle.resolucion === 'estimado' ? 'text-green-800' : 'text-red-800'}`}>
                  {recursoDetalle.resolucion === 'estimado' ? '✅ Recurso ESTIMADO' : '❌ Recurso DESESTIMADO'}
                </h3>
                {recursoDetalle.motivo_resolucion && (
                  <p className="text-sm">{recursoDetalle.motivo_resolucion}</p>
                )}
                {recursoDetalle.importe_devuelto > 0 && (
                  <p className="text-sm mt-2 font-medium text-green-700">Importe devuelto: {recursoDetalle.importe_devuelto}€</p>
                )}
              </div>
            )}

            {/* Texto del recurso */}
            {(recursoDetalle.texto_final || recursoDetalle.borrador_texto) && (
              <div className="border rounded-lg p-4">
                <button 
                  onClick={() => setExpandido(prev => ({ ...prev, texto: !prev.texto }))}
                  className="flex items-center justify-between w-full text-left"
                >
                  <h3 className="font-medium text-gray-700">Texto del Recurso</h3>
                  {expandido.texto ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </button>
                {expandido.texto && (
                  <pre className="mt-4 text-sm text-gray-600 whitespace-pre-wrap font-sans">
                    {recursoDetalle.texto_final || recursoDetalle.borrador_texto}
                  </pre>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Vista principal - Lista
  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-blue-600 hover:text-blue-800 mb-2">
              <ArrowLeft className="w-4 h-4" /> Volver
            </button>
            <h1 className="text-2xl font-bold text-gray-900">Gestión de Recursos</h1>
            <p className="text-gray-600">Recurra sus multas con ayuda de nuestros abogados</p>
          </div>
          <button
            onClick={() => navigate('/ayuda')}
            className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
          >
            <HelpCircle className="w-5 h-5" /> Ayuda
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader className="w-8 h-8 text-blue-500 animate-spin" />
          </div>
        ) : (
          <>
            {/* Estadísticas */}
            {estadisticas && (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                <div className="bg-white rounded-xl shadow-sm p-4">
                  <div className="text-3xl font-bold text-blue-600">{estadisticas.total || 0}</div>
                  <div className="text-sm text-gray-500">Total Recursos</div>
                </div>
                <div className="bg-white rounded-xl shadow-sm p-4">
                  <div className="text-3xl font-bold text-yellow-600">{parseInt(estadisticas.en_revision || 0) + parseInt(estadisticas.solicitados || 0)}</div>
                  <div className="text-sm text-gray-500">En Proceso</div>
                </div>
                <div className="bg-white rounded-xl shadow-sm p-4">
                  <div className="text-3xl font-bold text-green-600">{estadisticas.estimados || 0}</div>
                  <div className="text-sm text-gray-500">Estimados</div>
                </div>
                <div className="bg-white rounded-xl shadow-sm p-4">
                  <div className="text-3xl font-bold text-green-600">{estadisticas.tasa_exito_real || 0}%</div>
                  <div className="text-sm text-gray-500">Tasa de Éxito</div>
                </div>
                <div className="bg-white rounded-xl shadow-sm p-4">
                  <div className="text-3xl font-bold text-green-600">{estadisticas.total_devuelto || 0}€</div>
                  <div className="text-sm text-gray-500">Recuperado</div>
                </div>
              </div>
            )}

            {/* Multas pendientes para recurrir */}
            {multasPendientes.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
                <h2 className="text-lg font-bold text-gray-900 mb-4">Multas Disponibles para Recurrir</h2>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Matrícula</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Fecha</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Descripción</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Importe</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Puntos</th>
                        <th className="text-right py-3 px-4 text-sm font-medium text-gray-500">Acción</th>
                      </tr>
                    </thead>
                    <tbody>
                      {multasPendientes.slice(0, 5).map(multa => (
                        <tr key={multa.id} className="border-b hover:bg-gray-50">
                          <td className="py-3 px-4 font-medium">{multa.matricula}</td>
                          <td className="py-3 px-4">{new Date(multa.fecha_infraccion).toLocaleDateString()}</td>
                          <td className="py-3 px-4 text-sm text-gray-600 max-w-xs truncate">{multa.descripcion || '-'}</td>
                          <td className="py-3 px-4 font-medium">{multa.importe}€</td>
                          <td className="py-3 px-4">{multa.puntos || 0}</td>
                          <td className="py-3 px-4 text-right">
                            <button
                              onClick={() => analizarMulta(multa)}
                              className="px-3 py-1 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 text-sm font-medium"
                            >
                              Analizar Recurso
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Lista de recursos */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Mis Recursos</h2>
              {recursos.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Scale className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No tiene recursos solicitados</p>
                  <p className="text-sm">Seleccione una multa para analizar su viabilidad</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">ID</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Matrícula</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Fecha Multa</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Importe</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Probabilidad</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Estado</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Resolución</th>
                        <th className="text-right py-3 px-4 text-sm font-medium text-gray-500">Acción</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recursos.map(recurso => (
                        <tr key={recurso.id} className="border-b hover:bg-gray-50">
                          <td className="py-3 px-4 font-medium">#{recurso.id}</td>
                          <td className="py-3 px-4">{recurso.matricula}</td>
                          <td className="py-3 px-4">{new Date(recurso.fecha_infraccion).toLocaleDateString()}</td>
                          <td className="py-3 px-4">{recurso.importe_multa}€</td>
                          <td className="py-3 px-4">
                            <span className={`font-medium ${getProbabilidadColor(recurso.probabilidad_exito)}`}>
                              {recurso.probabilidad_exito}%
                            </span>
                          </td>
                          <td className="py-3 px-4">{getEstadoBadge(recurso.estado)}</td>
                          <td className="py-3 px-4">
                            {recurso.resolucion ? (
                              <span className={`text-sm font-medium ${recurso.resolucion === 'estimado' ? 'text-green-600' : 'text-red-600'}`}>
                                {recurso.resolucion === 'estimado' ? '✅ Estimado' : '❌ Desestimado'}
                              </span>
                            ) : '-'}
                          </td>
                          <td className="py-3 px-4 text-right">
                            <button
                              onClick={() => verDetalleRecurso(recurso)}
                              className="px-3 py-1 text-blue-600 hover:bg-blue-50 rounded-lg text-sm"
                            >
                              Ver Detalle
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default Recursos;
